const googleAnalyticsMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

let googleAnalyticsInitialized = false;
let googleAnalyticsScriptPromise: Promise<void> | null = null;

export function isGoogleAnalyticsEnabled(): boolean {
    return Boolean(googleAnalyticsMeasurementId);
}

function ensureGtagBase() {
    window.dataLayer = window.dataLayer || [];
    window.gtag =
        window.gtag ||
        function gtag() {
            // gtag.js requires the raw `arguments` object on the queue — it reads
            // each entry's arguments-like shape, so pushing a rest array instead
            // silently breaks command processing. This is why the official Google
            // snippet is written this way; prefer-rest-params cannot apply here.
            // eslint-disable-next-line prefer-rest-params -- official gtag queue semantics
            window.dataLayer.push(arguments);
        };
}

function loadGoogleAnalyticsScript(): Promise<void> {
    if (googleAnalyticsScriptPromise) {
        return googleAnalyticsScriptPromise;
    }

    googleAnalyticsScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}"]`
        );

        if (existingScript) {
            if (existingScript.dataset.loaded === 'true') {
                resolve();
                return;
            }

            existingScript.addEventListener(
                'load',
                () => {
                    existingScript.dataset.loaded = 'true';
                    resolve();
                },
                { once: true }
            );
            existingScript.addEventListener(
                'error',
                () => reject(new Error('Failed to load Google Analytics script')),
                { once: true }
            );
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}`;
        script.addEventListener(
            'load',
            () => {
                script.dataset.loaded = 'true';
                resolve();
            },
            { once: true }
        );
        script.addEventListener('error', () => reject(new Error('Failed to load Google Analytics script')), {
            once: true
        });
        document.head.appendChild(script);
    });

    return googleAnalyticsScriptPromise;
}

export async function ensureGoogleAnalyticsInit(): Promise<void> {
    if (!isGoogleAnalyticsEnabled()) {
        return;
    }

    ensureGtagBase();
    await loadGoogleAnalyticsScript();

    if (!googleAnalyticsInitialized) {
        window.gtag?.('js', new Date());
        window.gtag?.('config', googleAnalyticsMeasurementId, {
            send_page_view: false
        });
        googleAnalyticsInitialized = true;
    }
}

export async function captureGoogleAnalyticsPageview(
    pathname: string,
    search: string,
    title?: string | null
): Promise<void> {
    if (!isGoogleAnalyticsEnabled()) {
        return;
    }

    await ensureGoogleAnalyticsInit();

    window.gtag?.('event', 'page_view', {
        page_title: title ?? document.title,
        page_location: `${window.location.origin}${pathname}${search}`,
        page_path: `${pathname}${search}`
    });
}
